import { getFreshJWT } from '@api/interceptors';
import { useLoginMutation } from '@config/state/globalSlices/apiSlice/requests';
import { getIsLoggedIn, loginHandler } from '@config/state/globalSlices/userSlice';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { createNotification } from '@shared/utils/createNotification';
import { loginSchema } from 'compositions/auth/Form/schemas';
import { Form, Formik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation,useNavigate } from 'react-router-dom';

import AuthHeader from '../Components/AuthHeader';
import Input from '../Components/Input';
import SubmitButton from '../Components/SubmitButton';

const initialValues = {
  email: '',
  password: '',
};

export default function Login () {

  const navigate = useNavigate();
  const isLoggedIn = useSelector(getIsLoggedIn);
  const location = useLocation();

  useEffect(() => {
    if (isLoggedIn) {
      const redirectUrl = location?.state?.redirect;

      navigate(redirectUrl ?? '/locations')
    }
  }, [isLoggedIn, location?.state?.redirect, navigate]);

  //Login logic
  const dispatch = useDispatch();

  const [login, { isSuccess, isLoading, data, isError }] = useLoginMutation();

  const [username, setUsername] = useState('');
  const [token, setToken] = useState<string>();
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);
  const media = useMediaQuery('(max-width: 600px)')

  let label = 'Log in';

  const onVerify = useCallback(
    (token: string) => {
      setToken(token);
    },
    [setToken]
  );

  const handleSubmit = useCallback(
    (values: typeof initialValues) => {
      if (!token) {return}

      setUsername(values.email);
      login({ recaptchaToken: token, username: values.email, password: values.password });

      setRefreshReCaptcha((n) => !n);
    },
    [login, token]
  );

  useEffect(() => {
    if (!isSuccess || !data) {return;}

    if (data.result.ChallengeName) {
      if (data.result.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
        const sessionId = data.result?.Session;
        navigate('/ap/onboarding', { state: { sessionId, username } });
      }

      if (data.result.ChallengeName === 'RESET_REQUIRED') {
        navigate('/ap/forgot-password');
        createNotification({
          message: 'Password reset required',
          type: 'error',
          position: media ? 'top-center' :'bottom-center',
        });
      }
    } else {
      if (data.result.AuthenticationResult) {
        dispatch(loginHandler(data.result.AuthenticationResult));
        getFreshJWT();

        navigate('/locations');
      }
    }
  }, [isSuccess, data, username, dispatch, navigate, media]);

  useEffect(() => {
    if (isError) {
      createNotification({
        message: `Username and password combination is not recognised,
        if you have already followed the instructions at the top of this page,
        please contact your account manager who will be happy to assist you.`,
        type: 'error',
        duration: 5000,
        position: media ? 'top-center' :'bottom-center',

      });
    }

  }, [isError, media])

  if (!isLoading && !isSuccess) {
    label = 'Log in';
  } else if (isLoading) {
    label = 'Logging in...';
  } else if (isSuccess) {
    label = 'Login successful!';
  }

  return (
    <div className="w-full">
      <Helmet>
        <title>PHMG | Portal</title>
        <meta name="description" content="Welcome to the PHMG Client Portal." />
      </Helmet>
      <GoogleReCaptcha onVerify={onVerify} refreshReCaptcha={refreshReCaptcha} />
      <div className='flex flex-col align-middle justify-center'>
        <AuthHeader title="Log into your account" />
        <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={loginSchema}>
          <Form className='text-[14px]'>
            <Input label="Email or username" name='email' placeholder="john.smith@phmg.com" type="text" />
            <Input label="Password" name="password" showToggle placeholder="" type="password" forgotPassword />
            <SubmitButton text={label} type="submit" icon/>
          </Form>
        </Formik>
        <p className='text-[16px] mt-4 !font-[300]'>Don’t have an account? Contact <a className='text-[#FF609E] !underline' href='mailto:clientservices@phmg.com'>Client Services</a> to request an account</p>

      </div>
    </div>)

}
